import { useEffect, useRef } from "react"
import { Pause, Play } from "lucide-react"
import { useAmbientMotion } from "@/components/AmbientMotion"
import { useSkyClock } from "@/components/SkyClock"
import { personalInfo } from "@/config/personal"
import { buildSky, drawSky, placeInSky, spawnFlock, type Flock, type FrameInput, type Sky } from "./draw"
import { moonState, nightness, sunPosition } from "./sky"

/** Ambient frame rate; drifting hairlines don't need more. */
const FPS = 30
/** Time constant (s) for the theme fade; matches the CSS fade. */
const THEME_TAU = 0.4
/** Seconds between flocks while the sun is up. */
const FLOCK_GAP = [8, 18] as const
/** Birds stay home once the sun is this far below the horizon. */
const BIRDS_BELOW = -6

/**
 * How golden the hour is: 1 with the sun just under the horizon, gone by
 * 10° up, and lingering through twilight until 15° down, so dusk keeps an
 * amber glow after the page has turned dark.
 */
function warmthAt(sunElevation: number): number {
  const fromPeak = sunElevation + 1
  return Math.max(0, 1 - (fromPeak > 0 ? fromPeak / 11 : -fromPeak / 14))
}

/**
 * A hairline sky across the top of the page: drifting clouds, a flock of
 * birds by day, stars and the moon by night. Follows the real sky over
 * Bangkok through SkyClock. Decorative, except for its pause control.
 */
export function TopSky({ column }: { readonly column: number }) {
  const { paused, setPaused } = useAmbientMotion()
  const { now, theme } = useSkyClock()
  const wrapRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Read by the running loop, so a route change doesn't restart it.
  const columnRef = useRef(column)
  useEffect(() => {
    columnRef.current = column
  }, [column])
  // Survives effect restarts (pause, theme change), so nothing jumps.
  const motionRef = useRef({
    clock: 0,
    dark: theme === "dark" ? 1 : 0,
    flock: null as Flock | null,
    nextFlock: 2,
  })

  useEffect(() => {
    const wrap = wrapRef.current
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!wrap || !canvas || !ctx) return

    const motion = motionRef.current
    const darkTarget = theme === "dark" ? 1 : 0
    const { lat, lon } = personalInfo.coordinates
    let sky: Sky = buildSky(1, 1)
    let lastTick: number | null = null
    let lastDraw = 0
    let frame: number | null = null
    let visible = true

    // The sky's state for this moment; cheap enough to read every frame.
    const frameInput = (): FrameInput => {
      const date = now()
      const sun = sunPosition(date, lat, lon)
      const moon = moonState(date, lat, lon)
      const sunAt = placeInSky(sun.elevation, sun.azimuth, sky.width, sky.height)
      const moonAt = placeInSky(moon.elevation, moon.azimuth, sky.width, sky.height)
      return {
        sky,
        flock: motion.flock,
        clock: motion.clock,
        dark: motion.dark,
        night: nightness(sun.elevation),
        scroll: window.scrollY,
        column: columnRef.current,
        sun: sun.elevation > -2 ? sunAt : null,
        warmth: warmthAt(sun.elevation),
        dawn: sun.azimuth < 180,
        // After sunset the glow stays on the horizon where the sun went down.
        glowAt: placeInSky(Math.max(0, sun.elevation), sun.azimuth, sky.width, sky.height),
        moon:
          moon.elevation > 0 && moon.illumination > 0.08
            ? { ...moonAt, illumination: moon.illumination, litSide: sunAt.x >= moonAt.x ? 1 : -1 }
            : null,
      }
    }

    const draw = () => drawSky(ctx, frameInput())

    const resize = () => {
      const dpr = window.devicePixelRatio || 1
      const { width, height } = wrap.getBoundingClientRect()
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      sky = buildSky(width, height)
      // Start with a flock already crossing, if birds are out at this hour.
      const birdsOut = sunPosition(now(), lat, lon).elevation > BIRDS_BELOW
      if (!motion.flock && motion.clock === 0 && birdsOut) {
        motion.flock = { ...spawnFlock(width, height), x: width * 0.62 }
      }
      draw()
    }

    const tick = (t: number) => {
      const dt = lastTick === null ? 0 : Math.min((t - lastTick) / 1000, 0.1)
      lastTick = t
      motion.clock += dt

      const ease = (tau: number) => 1 - Math.exp(-dt / tau)
      motion.dark += (darkTarget - motion.dark) * ease(THEME_TAU)
      if (Math.abs(darkTarget - motion.dark) < 0.002) motion.dark = darkTarget

      // Birds: one flock at a time, while the sun is up.
      const flock = motion.flock
      if (flock) {
        flock.x += flock.dir * flock.speed * dt
        if (flock.x < -120 || flock.x > sky.width + 120) motion.flock = null
      } else if ((motion.nextFlock -= dt) <= 0) {
        motion.nextFlock = FLOCK_GAP[0] + Math.random() * (FLOCK_GAP[1] - FLOCK_GAP[0])
        if (sunPosition(now(), lat, lon).elevation > BIRDS_BELOW) {
          motion.flock = spawnFlock(sky.width, sky.height)
        }
      }

      if (t - lastDraw >= 1000 / FPS) {
        lastDraw = t
        draw()
      }
      frame = requestAnimationFrame(tick)
    }

    const start = () => {
      if (frame === null && visible) {
        lastTick = null
        frame = requestAnimationFrame(tick)
      }
    }
    const stop = () => {
      if (frame !== null) cancelAnimationFrame(frame)
      frame = null
    }

    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(wrap)

    if (paused) {
      // Still frame, kept to the clock and the theme.
      motion.dark = darkTarget
      draw()
      const refresh = setInterval(draw, 60_000)
      return () => {
        resizeObserver.disconnect()
        clearInterval(refresh)
      }
    }

    const intersectionObserver = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? true
      if (visible) start()
      else stop()
    })
    intersectionObserver.observe(wrap)
    start()

    return () => {
      stop()
      resizeObserver.disconnect()
      intersectionObserver.disconnect()
    }
  }, [paused, now, theme])

  return (
    <div ref={wrapRef} className="top-sky">
      <canvas ref={canvasRef} className="top-sky-canvas" aria-hidden="true" />
      <button
        type="button"
        className="top-sky-toggle"
        aria-label="Pause animation"
        aria-pressed={paused}
        onClick={() => setPaused(!paused)}
      >
        {paused ? <Play size={12} strokeWidth={2} /> : <Pause size={12} strokeWidth={2} />}
      </button>
    </div>
  )
}
